import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { COMPETENCIES, fmtDate, ROADS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { OutcomeBadge } from "@/components/StatusBadge";
import { Textarea } from "@/components/ui/textarea";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b py-2.5 text-sm last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-medium">{value}</span>
  </div>
);
const yn = (v) => (v ? "Yes" : "No");

const ReviewBox = ({ a, onDone }) => {
  const [notes, setNotes] = useState("");
  const decide = async (decision) => {
    try {
      const { data } = await api.post(`/assessments/${a.id}/review`, { decision, notes });
      toast.success("Trustee decision recorded");
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <section className="no-print space-y-3 rounded-2xl border-2 border-amber-500/40 bg-amber-500/5 p-5" data-testid="review-box">
      <h3 className="font-bold">Trustees & Lead Assessor decision</h3>
      <Textarea rows={3} placeholder="Decision notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="review-notes" />
      <div className="flex flex-wrap gap-3">
        <button data-testid="review-further-btn" onClick={() => decide("further_assessment")} className="rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground">Offer further assessment</button>
        <button data-testid="review-discontinue-btn" onClick={() => decide("discontinue")} className="rounded-full border border-red-500/50 px-5 py-2.5 text-sm font-bold text-red-300 hover:bg-red-500/10">Do not continue</button>
      </div>
    </section>
  );
};

export default function AssessmentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/assessments/${id}`).then((r) => setA(r.data)).catch(() => setMissing(true));
  }, [id]);

  if (missing) return <p data-testid="assessment-not-found" className="text-sm text-muted-foreground">Assessment not found.</p>;

  if (!a) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  const back = user.role === "rider" ? "/my" : `/riders/${a.rider_id}`;

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div className="no-print flex items-center justify-between">
        <Link to={back} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</Link>
        <button data-testid="print-assessment-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold hover:bg-secondary"><Printer className="h-4 w-4" /> Print</button>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src="/brand/logo.png" alt="" className="h-14 w-14" />
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Rider assessment report</p>
            <h1 data-testid="assessment-rider-name" className="text-2xl font-extrabold tracking-tight sm:text-3xl">{a.rider_name}</h1>
          </div>
        </div>
        <OutcomeBadge outcome={a.outcome} testId="assessment-outcome" />
      </div>

      {a.reasons?.length > 0 && (
        <section className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5 text-sm">
          <h3 className="mb-2 font-bold">Reasons</h3>
          <ul className="list-disc space-y-1 pl-5">{a.reasons.map((r) => <li key={r}>{r.replace(/: (\w+)$/, (m, k) => `: ${COMPETENCIES.find((c) => c.key === k)?.title || k}`)}</li>)}</ul>
        </section>
      )}
      {a.review && (
        <section className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 text-sm" data-testid="review-result">
          <h3 className="font-bold">Trustee decision: {a.review.decision === "further_assessment" ? "Further assessment offered" : "Not continuing"}</h3>
          <p className="mt-1 text-muted-foreground">{a.review.notes || "No notes"} — {a.review.by}, {fmtDate(a.review.at)}</p>
        </section>
      )}
      {a.outcome === "referred" && !a.review && user.role === "admin" && <ReviewBox a={a} onDone={setA} />}

      <div className="grid gap-6 md:grid-cols-2">
        <section className="rounded-2xl border bg-card p-5">
          <h3 className="mb-2 font-bold">Ride details</h3>
          <Row label="Date" value={fmtDate(a.date)} />
          <Row label="Type" value={a.assessment_type === "initial" ? "Initial assessment" : "Re-assessment"} />
          <Row label="Assessor" value={a.assessor_name} />
          <Row label="Location" value={a.location || "—"} />
          <Row label="Motorcycle" value={a.bike_details || "—"} />
          <Row label="Conditions" value={a.weather || "—"} />
          <Row label="Duration" value={a.duration_minutes ? `${a.duration_minutes} min` : "—"} />
          <Row label="Roads" value={a.roads.map((k) => ROADS.find((r) => r.key === k)?.label).join(", ") || "—"} />
          <Row label="Next due" value={<span className="font-mono">{fmtDate(a.next_due_date)}</span>} />
        </section>
        <section className="rounded-2xl border bg-card p-5">
          <h3 className="mb-2 font-bold">Pre-checks</h3>
          <Row label="Identity verified" value={`${yn(a.id_verified)}${a.id_type ? ` (${a.id_type})` : ""}`} />
          <Row label="DVSA eyesight (20m)" value={a.eyesight_pass ? "Passed" : "Failed"} />
          <Row label="Disclaimer signed" value={yn(a.disclaimer_signed)} />
          <Row label="Equipment appropriate" value={yn(a.equipment_ok)} />
          <Row label="Recording permission" value={yn(a.recording_permission)} />
          <Row label="Unsafe / illegal behaviour" value={yn(a.unsafe_behaviour)} />
          {a.signature && (
            <div className="mt-3">
              <div className="text-xs text-muted-foreground">Rider signature — {a.rider_signed_name}</div>
              <img src={a.signature} alt="Rider signature" data-testid="assessment-signature" className="mt-1 h-24 rounded-lg bg-white object-contain p-1" />
            </div>
          )}
        </section>
      </div>

      {a.eyesight_pass && (
        <section className="rounded-2xl border bg-card p-5" data-testid="assessment-competencies">
          <h3 className="mb-3 font-bold">Competencies</h3>
          {(a.competency_list || COMPETENCIES).map((c) => {
            const s = a.competencies[c.key];
            return (
              <div key={c.key} className="flex items-start justify-between gap-4 border-b py-3 last:border-0">
                <div>
                  <div className="font-semibold">{c.title}</div>
                  {s?.notes && <div className="text-sm text-muted-foreground">{s.notes}</div>}
                </div>
                <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 font-mono text-xs font-semibold ${s?.score === 1 ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                  {s?.score === 1 ? "1 · Advanced" : "2 · Not advanced"}
                </span>
              </div>
            );
          })}
        </section>
      )}

      {a.custom_answers?.length > 0 && (
        <section className="rounded-2xl border bg-card p-5" data-testid="assessment-custom-answers">
          <h3 className="mb-2 font-bold">Additional questions</h3>
          {a.custom_answers.map((q) => <Row key={q.id} label={q.label} value={q.kind === "yes_no" ? (q.answer || "—").toUpperCase() : q.text || "—"} />)}
        </section>
      )}

      {(a.unsafe_details || a.highway_code_notes || a.feedback) && (
        <section className="space-y-3 rounded-2xl border bg-card p-5 text-sm">
          {a.unsafe_details && <div><div className="font-bold">Unsafe / illegal behaviour</div><p className="text-muted-foreground">{a.unsafe_details}</p></div>}
          {a.highway_code_notes && <div><div className="font-bold">Highway Code questions</div><p className="text-muted-foreground">{a.highway_code_notes}</p></div>}
          {a.feedback && <div><div className="font-bold">Feedback</div><p className="whitespace-pre-wrap text-muted-foreground">{a.feedback}</p></div>}
        </section>
      )}
    </div>
  );
}
